import React, { useEffect, useState } from "react";
import api, { API_URL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Package, Download, Copy, Check, Loader2, ShieldCheck, ExternalLink } from "lucide-react";

export default function PluginPage() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/plugin/wordpress/info")
      .then(({ data }) => setInfo(data))
      .catch(() => toast.error("Erro ao consultar o plugin"))
      .finally(() => setLoading(false));
  }, []);

  async function download() {
    setDownloading(true);
    try {
      const token = localStorage.getItem("nia_token");
      const resp = await fetch(`${API_URL}/plugin/wordpress/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Falha ao baixar");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "nia-chatbot.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download iniciado");
    } catch (e) {
      toast.error(e.message || "Erro");
    } finally { setDownloading(false); }
  }

  function copyShortcode() {
    navigator.clipboard.writeText('[nia_chatbot setor="secretaria-academica"]');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Plugin WordPress"
        subtitle="Baixe e instale o plugin oficial do NIA no site WordPress da sua instituição. Widget flutuante seguro contra CSP."
      />

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <Card className="p-8 mb-6 bg-gradient-to-br from-[#e6f0fa] via-white to-white border-[#cce0f5]">
            <div className="flex items-start gap-6">
              <div className="shrink-0 w-16 h-16 rounded-xl bg-[#1e5a8e] text-white flex items-center justify-center">
                <Package className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="font-[Outfit] text-2xl font-semibold text-slate-900">NIA Chatbot Plugin</h2>
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">v1.0.0</Badge>
                </div>
                <p className="text-sm text-slate-600 mb-4 max-w-lg">
                  Plugin PHP self-contained para WordPress 6+. Widget flutuante com proxy REST server-a-server,
                  totalmente <b>CSP-safe</b>: o navegador nunca chama o backend externo.
                </p>
                <div className="flex flex-wrap gap-3 items-center">
                  <Button
                    data-testid="download-plugin-btn"
                    onClick={download}
                    disabled={!info?.available || downloading}
                    className="bg-[#1e5a8e] hover:bg-[#164570]"
                  >
                    {downloading
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Baixando…</>
                      : <><Download className="w-4 h-4 mr-2" /> Baixar nia-chatbot.zip</>}
                  </Button>
                  {info?.available && (
                    <span className="text-xs text-slate-500">{info.size_kb} KB</span>
                  )}
                </div>
                {!info?.available && (
                  <p className="text-xs text-amber-700 mt-3">
                    ⚠ Plugin não encontrado no servidor. Contate o desenvolvedor para gerar o build.
                  </p>
                )}
              </div>
            </div>
          </Card>

          <Card className="p-6 mb-6">
            <h3 className="font-[Outfit] font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#1e5a8e]" /> Como funciona (arquitetura CSP-safe)
            </h3>
            <pre className="text-xs bg-slate-50 border border-slate-200 rounded-md p-4 font-mono text-slate-700 leading-relaxed overflow-x-auto">
{`[ Navegador do visitante ]
       │  (same-origin: WordPress)
       ▼
[ /wp-json/nia-chatbot/v1/mensagem ]
       │  (server-a-server via wp_remote_post)
       ▼
[ ${API_URL}/chat/{slug}/message ]`}
            </pre>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              O JavaScript do widget é servido pelo próprio WordPress. O visitante nunca faz requisição
              cross-origin — quem chama o backend Python é o WordPress, no servidor. Isso funciona mesmo
              com <code>Content-Security-Policy</code> restritiva bloqueando <code>connect-src</code> externos.
            </p>
          </Card>

          <Card className="p-6 mb-6">
            <h3 className="font-[Outfit] font-semibold text-slate-900 mb-4">Passo a passo da instalação</h3>
            <ol className="space-y-3 text-sm text-slate-700">
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#e6f0fa] text-[#1e5a8e] flex items-center justify-center text-xs font-semibold">1</span>
                <span>Baixe o arquivo <code>nia-chatbot.zip</code> clicando no botão acima.</span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#e6f0fa] text-[#1e5a8e] flex items-center justify-center text-xs font-semibold">2</span>
                <span>No painel do WordPress: <b>Plugins → Adicionar → Enviar plugin</b> e selecione o zip.</span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#e6f0fa] text-[#1e5a8e] flex items-center justify-center text-xs font-semibold">3</span>
                <span>Ative o plugin.</span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#e6f0fa] text-[#1e5a8e] flex items-center justify-center text-xs font-semibold">4</span>
                <span>
                  Vá em <b>Configurações → NIA Chatbot</b>. Cole a URL do backend:{" "}
                  <code className="bg-slate-100 px-2 py-0.5 rounded">{process.env.REACT_APP_BACKEND_URL}</code>
                  {" "}e informe o slug do setor padrão (ex.: <code>secretaria-academica</code>).
                </span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#e6f0fa] text-[#1e5a8e] flex items-center justify-center text-xs font-semibold">5</span>
                <span>Marque <b>"Bolha global no footer"</b> para exibir em todas as páginas. Pronto!</span>
              </li>
            </ol>
          </Card>

          <Card className="p-6">
            <h3 className="font-[Outfit] font-semibold text-slate-900 mb-3">Formas de embutir no conteúdo</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Shortcode</p>
                <div className="flex gap-2">
                  <code className="flex-1 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm font-mono text-slate-800">
                    [nia_chatbot setor="secretaria-academica"]
                  </code>
                  <Button variant="outline" size="sm" onClick={copyShortcode} data-testid="copy-shortcode-btn">
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Classe CSS em qualquer botão</p>
                <code className="block bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm font-mono text-slate-800 whitespace-pre-wrap">
{`<button class="nia-chatbot-trigger" data-setor="secretaria-academica">
  Fale com a Secretaria
</button>`}
                </code>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
